import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import valenciaFussball from '@/assets/team/valencia-fussball.jpg.asset.json';
import valenciaCity from '@/assets/team/valencia-city.jpg.asset.json';
import valenciaBoat from '@/assets/team/valencia-boat.jpg.asset.json';
import karting from '@/assets/team/karting.jpg.asset.json';
import lasertag from '@/assets/team/lasertag.jpg.asset.json';
import dinner1 from '@/assets/team/dinner-1.jpg.asset.json';
import dinner2 from '@/assets/team/dinner-2.jpg.asset.json';
import barcelonaDinner from '@/assets/team/barcelona-dinner.jpg.asset.json';
import stageKickoff from '@/assets/team/stage-kickoff.jpg.asset.json';
import stageSpeaker from '@/assets/team/stage-speaker.jpg.asset.json';
import alpsTerrace from '@/assets/team/alps-terrace.jpg.asset.json';
import bowling from '@/assets/team/bowling.jpg.asset.json';
import waterfall from '@/assets/team/waterfall.jpg.asset.json';
import hummer from '@/assets/team/hummer.jpg.asset.json';
import helicopter from '@/assets/team/helicopter.jpg.asset.json';
import ibizaBoat from '@/assets/team/ibiza-boat.jpg.asset.json';
import seminar from '@/assets/team/seminar.jpg.asset.json';
import kartingTrack from '@/assets/team/karting-track.jpg.asset.json';
import barcelonaYachtCrew from '@/assets/team/barcelona-yacht-crew.jpg.asset.json';
import barcelonaYachtAnchor from '@/assets/team/barcelona-yacht-anchor.jpg.asset.json';
import barcelonaJetski from '@/assets/team/barcelona-jetski.jpg.asset.json';

export const teamSlides = [
  { url: valenciaCity.url, caption: 'Teamreise Valencia', alt: 'HCP-Team vor dem Valencia-Schriftzug' },
  { url: valenciaBoat.url, caption: 'Bootstour in Valencia', alt: 'HCP-Team am Steg vor einem Segelboot in Valencia' },
  { url: valenciaFussball.url, caption: 'Fußballturnier in Valencia', alt: 'HCP-Team beim Fußballturnier im Puma-Stadion' },
  { url: barcelonaDinner.url, caption: 'Dinner über Barcelona', alt: 'Abendessen auf einer Terrasse mit Blick über Barcelona' },
  { url: barcelonaYachtCrew.url, caption: 'Yacht-Incentive Barcelona', alt: 'HCP-Team an Bord der Yacht beim Barcelona Incentive' },
  { url: barcelonaYachtAnchor.url, caption: 'Badestopp vor der Küste', alt: 'Yacht vor Anker mit schwimmendem HCP-Team im Mittelmeer' },
  { url: barcelonaJetski.url, caption: 'Jetski im Sonnenuntergang', alt: 'Jetski-Fahrt vor der Skyline im Sonnenuntergang' },
  { url: stageKickoff.url, caption: 'Kickoff-Event', alt: 'HCP-Team auf der Kongress-Bühne "The Future is (Y)ours"' },
  { url: stageSpeaker.url, caption: 'Auf der Bühne', alt: 'Zwei Redner auf der Kongress-Bühne vor dem Team' },
  { url: dinner1.url, caption: 'Team-Dinner Berlin', alt: 'Gemeinsames Abendessen des HCP-Teams' },
  { url: dinner2.url, caption: 'Lange Tafel, langer Abend', alt: 'HCP-Team an langer Tafel im Restaurant' },
  { url: karting.url, caption: 'Kart-Session', alt: 'HCP-Team beim gemeinsamen Kartfahren' },
  { url: kartingTrack.url, caption: 'Auf der Kartbahn', alt: 'HCP-Team in Karts auf der Rennstrecke' },
  { url: lasertag.url, caption: 'Lasertag-Abend', alt: 'HCP-Team beim Lasertag' },
  { url: bowling.url, caption: 'Bowling-Abend', alt: 'HCP-Team beim Bowling' },
  { url: alpsTerrace.url, caption: 'Strategie in den Alpen', alt: 'HCP-Team auf einer Terrasse mit Bergpanorama' },
  { url: seminar.url, caption: 'Seminar & Reflexion', alt: 'Seminar mit Flipchart zu Eigenverantwortung und Selbstreflektion' },
  { url: helicopter.url, caption: 'Helikopter-Incentive', alt: 'HCP-Team vor einem Helikopter' },
  { url: ibizaBoat.url, caption: 'Yacht-Tag auf dem Mittelmeer', alt: 'HCP-Team auf einer Yacht bei Sonnenuntergang' },
  { url: hummer.url, caption: 'Roadtrip-Feeling', alt: 'HCP-Team vor Hummer-Limousine' },
  { url: waterfall.url, caption: 'Ausflug zum Wasserfall', alt: 'HCP-Team vor einem Wasserfall' },
];

const AUTO_MS = 4500;

interface TeamSlideshowProps {
  variant?: 'default' | 'compact';
  aspectClassName?: string;
}

const TeamSlideshow = ({ variant = 'default', aspectClassName }: TeamSlideshowProps) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const pauseTimer = useRef<number | null>(null);

  const go = useCallback((next: number) => {
    setIndex((next + teamSlides.length) % teamSlides.length);
  }, []);

  const pauseBriefly = useCallback(() => {
    setPaused(true);
    if (pauseTimer.current) window.clearTimeout(pauseTimer.current);
    pauseTimer.current = window.setTimeout(() => setPaused(false), 6000);
  }, []);

  useEffect(() => {
    if (paused) return;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setTimeout(() => go(index + 1), AUTO_MS);
    return () => clearTimeout(t);
  }, [index, paused, go]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 40) {
      go(index + (dx < 0 ? 1 : -1));
      pauseBriefly();
    }
  };

  const compact = variant === 'compact';
  const aspect = aspectClassName ?? 'aspect-[4/5] md:aspect-[4/3]';

  return (
    <div
      className={`relative w-full ${aspect} rounded-2xl overflow-hidden border border-primary/20 shadow-[0_20px_60px_hsl(227_78%_52%/0.18)] bg-foreground group`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <AnimatePresence mode="sync">
        <motion.img
          key={index}
          src={teamSlides[index].url}
          alt={teamSlides[index].alt}
          loading={index === 0 ? 'eager' : 'lazy'}
          fetchPriority={index === 0 ? 'high' : 'auto'}
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 0.9 }, scale: { duration: AUTO_MS / 1000 + 1, ease: 'linear' } }}
        />
      </AnimatePresence>

      {!compact && (
        <>
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3 pointer-events-none">
            <span className="inline-flex items-center rounded-full bg-background/80 backdrop-blur px-3 py-1.5 text-xs md:text-sm font-medium text-foreground">
              {teamSlides[index].caption}
            </span>
            <span className="text-xs text-white/80 tabular-nums">
              {String(index + 1).padStart(2, '0')} / {String(teamSlides.length).padStart(2, '0')}
            </span>
          </div>
        </>
      )}

      {/* Arrows — always visible, subtle on mobile */}
      <button
        type="button"
        aria-label="Vorheriges Bild"
        onClick={() => { go(index - 1); pauseBriefly(); }}
        className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background/60 md:bg-background/70 backdrop-blur text-foreground md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-background"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        aria-label="Nächstes Bild"
        onClick={() => { go(index + 1); pauseBriefly(); }}
        className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background/60 md:bg-background/70 backdrop-blur text-foreground md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-background"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Dots */}
      {!compact && (
        <div className="absolute top-4 right-4 flex items-center gap-1.5 max-w-[70%] flex-wrap justify-end">
          {teamSlides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Bild ${i + 1} anzeigen`}
              onClick={() => { go(i); pauseBriefly(); }}
              className="p-1.5 -m-1.5"
            >
              <span
                className={`block h-1.5 rounded-full transition-all ${
                  i === index ? 'w-6 bg-primary' : 'w-1.5 bg-white/60 hover:bg-white'
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamSlideshow;
